import React, { useState } from 'react';
import { Sparkles, Check, ChevronDown, X } from 'lucide-react';

export const CATEGORIZED_FORMATS = [
  {
    category: 'Pre-Primary & Kindergarten',
    description: 'Developmental rating rubrics with qualitative criteria (No hard numbers)',
    formats: [
      {
        key: 'kindergarten_progress',
        name: 'Kindergarten Developmental Progress',
        icon: '🧸',
        badge: 'Pre-Primary',
        description: 'Qualitative rating card for LKG/UKG across Language, Numeracy, Motor, Social & Art domains.',
        themeColor: '#8b5cf6',
        accentColor: '#ec4899',
        bestFor: 'LKG, UKG, Nursery & Playgroup'
      }
    ]
  },
  {
    category: 'Primary School (Classes 1 - 5)',
    description: 'Visual, skill-oriented progress report with domain ratings and attendance meter',
    formats: [
      {
        key: 'primary_progress',
        name: 'Primary Progress Card',
        icon: '🎒',
        badge: 'Primary 1-5',
        description: 'Child-friendly progress report for primary students with skill badges, attendance, and remarks.',
        themeColor: '#0d9488',
        accentColor: '#f43f5e',
        bestFor: 'Class 1 to Class 5'
      },
      {
        key: 'modern_school',
        name: 'Modern Continuous Assessment Card',
        icon: '🏫',
        badge: 'Neo-Corporate',
        description: 'Modern neo-corporate layout with ribbon header, score badges, and clean rounded cards.',
        themeColor: '#2563eb',
        accentColor: '#10b981',
        bestFor: 'CBSE / ICSE / Progressive Schools'
      }
    ]
  },
  {
    category: 'Middle & Secondary School (Classes 6 - 10)',
    description: 'Standard academic transcripts, term-based CBSE cards, and annual state-board records',
    formats: [
      {
        key: 'classic_academic',
        name: 'Classic Academic Progress Report',
        icon: '🎓',
        badge: 'Formal Traditional',
        description: 'Gold crest borders, formal school header, comprehensive subject grid, and official seal.',
        themeColor: '#1e3a8a',
        accentColor: '#f59e0b',
        bestFor: 'All Middle & High School Classes'
      },
      {
        key: 'term_based_progress',
        name: 'Term-Based / Periodic Exam Card',
        icon: '📑',
        badge: 'CBSE Matrix',
        description: 'Structured columns for Periodic Tests (PT), Notebook, Subject Enrichment (SEA) & Term Exams.',
        themeColor: '#15803d',
        accentColor: '#0284c7',
        bestFor: 'Classes 6 to 10 (CBSE Pattern)'
      },
      {
        key: 'annual_progress',
        name: 'Annual Cumulative Report',
        icon: '📊',
        badge: 'Year-End Comprehensive',
        description: 'Comprehensive annual progress card combining Term 1, Term 2, Grand Total & Promotion result.',
        themeColor: '#1e3a8a',
        accentColor: '#059669',
        bestFor: 'Annual Exam / Promotion Ledger'
      },
      {
        key: 'state_board_record',
        name: 'State Board Record Sheet',
        icon: '📜',
        badge: 'State Board Pattern',
        description: 'Traditional State Board evaluation ledger with monthly, half-yearly and yearly marks.',
        themeColor: '#b91c1c',
        accentColor: '#d97706',
        bestFor: 'State Board Syllabus'
      }
    ]
  }
];

const ALL_FORMAT_FLAT = CATEGORIZED_FORMATS.flatMap(c => c.formats);

export const getFormatByKey = (key) => {
  return ALL_FORMAT_FLAT.find(f => f.key === key) || ALL_FORMAT_FLAT[0];
};

const ReportCardFormatSelector = ({
  selectedFormat = 'classic_academic',
  onSelectFormat,
  compact = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const activeObj = getFormatByKey(selectedFormat);

  const handleSelect = (key) => {
    onSelectFormat(key);
    setIsOpen(false);
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: compact ? '6px 12px' : '8px 14px',
          background: '#ffffff',
          border: '1.5px solid #cbd5e1',
          borderRadius: '7px',
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          transition: 'all 0.15s ease'
        }}
        title="Choose Marks Card Format / Template"
      >
        <span style={{ fontSize: '15px' }}>{activeObj.icon}</span>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', lineHeight: '1' }}>
            Marks Card Format
          </div>
          <div style={{ fontSize: '12.5px', fontWeight: '800', color: '#0f172a', lineHeight: '1.3' }}>
            {activeObj.name}
          </div>
        </div>
        <ChevronDown size={14} color="#64748b" style={{ marginLeft: '4px' }} />
      </button>

      {/* Modal / Dialog for Categorized Formats */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(3px)',
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            maxWidth: '850px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              padding: '18px 24px',
              borderBottom: '1.5px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f8fafc',
              borderTopLeftRadius: '14px',
              borderTopRightRadius: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: '#eff6ff',
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Sparkles size={18} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                    Choose Marks Card Format / Template
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                    Select from school-specific formats tailored for Kindergarten, Primary, and High School standards.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '6px',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Content Body */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {CATEGORIZED_FORMATS.map((cat, idx) => (
                <div key={idx}>
                  <div style={{ marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '13.5px', fontWeight: '800', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      {cat.category}
                    </h4>
                    <p style={{ margin: '2px 0 0 0', fontSize: '11.5px', color: '#64748b' }}>
                      {cat.description}
                    </p>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                    {cat.formats.map(fmt => {
                      const isSelected = selectedFormat === fmt.key;
                      return (
                        <div
                          key={fmt.key}
                          onClick={() => handleSelect(fmt.key)}
                          style={{
                            padding: '14px',
                            borderRadius: '10px',
                            border: isSelected ? `2px solid ${fmt.themeColor}` : '1.5px solid #e2e8f0',
                            background: isSelected ? '#f8fafc' : '#ffffff',
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'all 0.15s ease',
                            boxShadow: isSelected ? `0 4px 12px ${fmt.themeColor}20` : 'none'
                          }}
                        >
                          {isSelected && (
                            <span style={{
                              position: 'absolute',
                              top: '10px',
                              right: '10px',
                              background: fmt.themeColor,
                              color: '#ffffff',
                              fontSize: '10px',
                              fontWeight: '800',
                              padding: '2px 7px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}>
                              <Check size={11} /> ACTIVE
                            </span>
                          )}

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <span style={{ fontSize: '22px' }}>{fmt.icon}</span>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a' }}>
                                {fmt.name}
                              </div>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: fmt.themeColor, textTransform: 'uppercase' }}>
                                {fmt.badge}
                              </span>
                            </div>
                          </div>

                          <p style={{ margin: '6px 0 10px 0', fontSize: '11.5px', color: '#64748b', lineHeight: '1.4' }}>
                            {fmt.description}
                          </p>

                          <div style={{ fontSize: '10.5px', fontWeight: '700', color: '#475569', background: '#f1f5f9', padding: '3px 8px', borderRadius: '4px', width: 'fit-content' }}>
                            🎯 {fmt.bestFor}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#f8fafc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottomLeftRadius: '14px',
              borderBottomRightRadius: '14px'
            }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                Current Active: <strong>{activeObj.name}</strong>
              </span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                style={{
                  padding: '7px 18px',
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default ReportCardFormatSelector;
